/**
 * Class to render a timeline widget for Security log
 */
class SpbcTimelineWidget {
    // Static constants
    static markerRadius = 4;
    static hoursRangeMax = 24;
    static axisStepsCount = 6;
    static userActionsUpstep = 12;
    static legendCircleX = -100;
    static legendTextX = -85;

    /**
     * Constructor.
     */
    constructor() {
        // Current time properties
        this.now = new Date();
        this.currentTimestamp = Math.floor(this.now.getTime() / 1000);

        // Data properties
        this.eventsData = null;
        this.timelineMarkersData = [];
        this.timelineKnownEventsData = [];
        this.timelineEventTooltip = null;
        this.timelineStartTimestamp = this.currentTimestamp - SpbcTimelineWidget.hoursRangeMax * 3600;
    }

    /**
     * Initialize and render the timeline widget
     */
    render() {
        if (typeof spbcTimelineWidgetData === 'undefined') {
            return;
        }

        this.timelineEventTooltip = document.getElementById('spbc-timeline-tooltip');
        this.eventsData = spbcTimelineWidgetData.users_actions;
        this.timelineStartTimestamp = Number(spbcTimelineWidgetData.first_event_timestamp);
        this.timelineKnownEventsData = spbcTimelineWidgetData.known_events_data;
        this.timelineMarkersData = this.generateMarkersData(spbcTimelineWidgetData.found_types);

        const activeTab = document.querySelector('.spbc_tab_section--active');
        if (activeTab && activeTab.id === 'spbc_tab-security_log') {
            this.initTimeline();
        }

        // Initialize timeline when tab is loaded
        window.addEventListener('spbctTabLoaded', (event) => {
            if (event.detail.tab === 'security_log') {
                this.initTimeline();
            }
        });

        // Handle window resize
        window.addEventListener('resize', () => {
            const userSelect = document.getElementById('spbc-timeline-user-select');
            if (userSelect.value) {
                this.drawUserTimeline(userSelect.value);
            }
        });
    }

    /**
     * Initialize timeline with user selection
     */
    initTimeline() {
        const userSelect = document.getElementById('spbc-timeline-user-select');
        const users = Object.keys(this.eventsData);

        // Reset user selection dropdown
        userSelect.innerHTML = '';

        // Populate user options
        users.forEach((user) => {
            userSelect.appendChild(new Option(user, user));
        });

        // Add selection handler
        userSelect.addEventListener('change', () => {
            this.drawUserTimeline(userSelect.value);
        });

        // Draw initial timeline or show message
        if (users.length > 0) {
            this.drawUserTimeline(users[0]);
        } else {
            document.querySelector('.spbc-timeline-container').style.display = 'none';
            document.querySelector('.spbc-timeline-controls').style.display = 'none';
            document.getElementById('spbc-timeline-no-data').style.display = 'block';
        }
    }

    /**
     * Draw time axis on the timeline
     * @param {SVGElement} container - SVG container element
     * @param {number} width - Drawing area width
     * @param {number} height - Drawing area height
     */
    drawTimeAxis(container, width, height) {
        const timeRange = this.currentTimestamp - this.timelineStartTimestamp;
        const axis = document.createElementNS('http://www.w3.org/2000/svg', 'line');

        // Create main axis line
        axis.setAttribute('class', 'spbc-timeline-axis');
        axis.setAttribute('x1', '0');
        axis.setAttribute('y1', height);
        axis.setAttribute('x2', width);
        axis.setAttribute('y2', height);
        container.appendChild(axis);

        // Create axis ticks and labels
        for (let i = 0; i <= SpbcTimelineWidget.axisStepsCount; i++) {
            const timestamp = this.timelineStartTimestamp + (i / SpbcTimelineWidget.axisStepsCount) * timeRange;
            const x = (i / SpbcTimelineWidget.axisStepsCount) * width;

            // Create tick mark
            const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            tick.setAttribute('class', 'spbc-timeline-axis');
            tick.setAttribute('x1', x);
            tick.setAttribute('y1', height);
            tick.setAttribute('x2', x);
            tick.setAttribute('y2', height + 5);
            container.appendChild(tick);

            // Create time label
            const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            label.setAttribute('class', 'spbc-timeline-axis-text');
            label.setAttribute('x', x);
            label.setAttribute('y', height + 20);

            const date = new Date(timestamp * 1000);
            label.textContent = `${date
                .getHours()
                .toString()
                .padStart(2, '0')}:${date.getMinutes()
                .toString()
                .padStart(2, '0')}`;
            container.appendChild(label);
        }
    }

    /**
     * Create SVG element for an event
     * @param {Object} event - Event data
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @param {string} user - User name
     * @return {SVGElement} Event group element
     */
    createEventElement(event, x, y, user) {
        const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        group.setAttribute('transform', `translate(${x}, ${y})`);
        group.setAttribute('data-event', JSON.stringify({...event, user}));

        // Create event marker
        const marker = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        marker.setAttribute('class', 'spbc-timeline-event-marker');
        marker.setAttribute('r', SpbcTimelineWidget.markerRadius);
        marker.style.fill = this.timelineMarkersData[event.event].color;
        group.appendChild(marker);

        // Add event handlers
        group.addEventListener('mouseenter', this.showEventTooltip.bind(this));
        group.addEventListener('mouseleave', this.hideEventTooltip.bind(this));

        return group;
    }

    /**
     * Show event tooltip
     * @param {Event} event - Mouse event
     */
    showEventTooltip(event) {
        const eventData = JSON.parse(event.currentTarget.getAttribute('data-event'));
        const {user, event: eventName, time} = eventData;

        // Format date for display
        const formatDate = (date) => date.toISOString().replace(/T/, ' ').replace(/\..+/, '');

        // Update tooltip content
        document.getElementById('spbc-timeline-tooltip--user').innerText = user;
        document.getElementById('spbc-timeline-tooltip--event').innerText = eventName;
        document.getElementById('spbc-timeline-tooltip--time').innerText = formatDate(new Date(time * 1000));

        // Show tooltip
        this.timelineEventTooltip.style.opacity = '1';
    }

    /**
     * Hide event tooltip
     */
    hideEventTooltip() {
        this.timelineEventTooltip.style.opacity = '0';
    }

    /**
     * Draw timeline for specific user
     * @param {string} user - Selected user name
     */
    drawUserTimeline(user) {
        const svg = document.getElementById('spbc-timeline-svg');
        const noDataMessage = document.getElementById('spbc-timeline-no-data');

        // Clear previous content
        svg.innerHTML = '';
        noDataMessage.style.display = 'none';

        // Calculate dimensions
        const width = svg.parentElement.clientWidth;
        const height = svg.parentElement.clientHeight;
        let additionalEventsHeight = 0;
        if (spbcTimelineWidgetData.found_types.length > 5) {
            additionalEventsHeight = (spbcTimelineWidgetData.found_types.length - 5) * 12;
        }
        svg.setAttribute('width', width);
        svg.setAttribute('height', height + additionalEventsHeight);

        // Setup drawing area
        const margin = {top: 30, right: 40, bottom: 50, left: 120};
        const contentWidth = width - margin.left - margin.right;
        const contentHeight = height - margin.top - margin.bottom;

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('transform', `translate(${margin.left}, ${margin.top})`);
        svg.appendChild(g);

        // Draw axis
        this.drawTimeAxis(g, contentWidth, contentHeight);

        // Filter user events
        const userEvents = (this.eventsData[user] || []).filter((event) =>
            event.time >= this.timelineStartTimestamp &&
            event.time <= this.currentTimestamp,
        );

        // Handle no events case
        if (userEvents.length === 0) {
            noDataMessage.style.display = 'block';
            return;
        }

        // Calculate base positions
        const baseLineY = contentHeight / 2 + additionalEventsHeight;
        const timeRange = this.currentTimestamp - this.timelineStartTimestamp;
        const uniqueEventsCount = spbcTimelineWidgetData.found_types.length;

        // Draw event lines and legends
        for (let i = 0; i < uniqueEventsCount; i++) {
            const userLineY = baseLineY - (SpbcTimelineWidget.userActionsUpstep * i);
            const eventType = spbcTimelineWidgetData.found_types[i];
            const color = this.timelineMarkersData[eventType].color;

            // Draw event line
            const userLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            userLine.setAttribute('class', 'spbc-timeline-user-line');
            userLine.setAttribute('x1', '0');
            userLine.setAttribute('y1', userLineY);
            userLine.setAttribute('x2', contentWidth);
            userLine.setAttribute('y2', userLineY);
            g.appendChild(userLine);

            // Draw legend circle
            const legendCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            legendCircle.setAttribute('class', 'spbc-timeline-legend-circle');
            legendCircle.setAttribute('cx', SpbcTimelineWidget.legendCircleX);
            legendCircle.setAttribute('cy', userLineY);
            legendCircle.setAttribute('r', SpbcTimelineWidget.markerRadius);
            legendCircle.style.fill = color;
            g.appendChild(legendCircle);

            // Draw legend text
            const legendText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            legendText.setAttribute('class', 'spbc-timeline-legend-text');
            legendText.setAttribute('x', SpbcTimelineWidget.legendTextX);
            legendText.setAttribute('y', userLineY);
            legendText.setAttribute('text-anchor', 'start');
            legendText.setAttribute('dominant-baseline', 'middle');
            legendText.textContent = eventType;
            g.appendChild(legendText);
        }

        // Draw events
        userEvents.forEach((userEvent) => {
            const x = ((userEvent.time - this.timelineStartTimestamp) / timeRange) * contentWidth;
            const eventY = baseLineY + this.timelineMarkersData[userEvent.event].upstep;
            g.appendChild(this.createEventElement(userEvent, x, eventY, user));
        });
    }

    /**
     * Generate marker data for events
     * @param {Array} events - List of event types
     * @return {Object} Marker configuration data
     */
    generateMarkersData(events) {
        const colorMap = {};
        let upstep = 0;
        let colorScheme = 'default';

        events.forEach((userEvent) => {
            if (this.timelineKnownEventsData[userEvent] && this.timelineKnownEventsData[userEvent].color_scheme) {
                colorScheme = this.timelineKnownEventsData[userEvent].color_scheme;
            }
            colorMap[userEvent] = {
                color: colorScheme,
                upstep: upstep,
            };
            upstep -= SpbcTimelineWidget.userActionsUpstep;
        });

        return colorMap;
    }
}

/**
 * Wrapper
 */
function spbcTimelineWidgetRender() { // eslint-disable-line no-unused-vars
    const timelineWidget = new SpbcTimelineWidget();
    timelineWidget.render();
}

